import { FREE_TRIAL_DAYS } from '@/config/credits.config';
import { env } from '@/env';
import type { PaymentConfig } from '@/types';

export interface PricingPlan {
  id: string;
  name: string;
  description: string;
  price: number; // Monthly price
  yearlyPrice: number; // Yearly price (total)
  interval: 'month' | 'year' | 'week';
  stripePriceIds: {
    monthly: string;
    yearly: string;
    weekly?: string;
    oneTime?: string;
  };
  credits: {
    monthly: number; // Credits on each recurring monthly payment
    yearly: number; // Credits on each recurring yearly payment
    weekly?: number; // Credits on each recurring weekly payment
    oneTime: number; // Credits on one-time payment
  };
  features: string[]; // Monthly features
  yearlyFeatures?: string[]; // Yearly features overrides
  popular: boolean;
  hidden?: boolean;
  limits: {
    storage: number;
    users: number;
    projects: number;
    apiCalls: number;
  };
}

const createPlanCredits = (monthly: number) => ({
  monthly,
  yearly: monthly * 12,
  oneTime: monthly,
});

export const paymentConfig: PaymentConfig = {
  // Payment provider
  provider: 'stripe',

  // Base currency
  currency: 'gbp',

  // Stripe configuration
  stripe: {
    apiVersion: env.NEXT_PUBLIC_STRIPE_API_VERSION,
  },

  // Subscription plans
  plans: [
    // Tier 1 - 250 Credits
    {
      id: 'tier-1',
      name: '250 Credits Monthly',
      price: 5,
      yearlyPrice: 48,
      interval: 'month',
      stripePriceIds: {
        monthly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER1_MONTHLY || '',
        yearly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER1_YEARLY || '',
        oneTime: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER1_ONETIME || '',
      },
      description: 'For casual creators',
      credits: createPlanCredits(250),
      features: ['Up to 8 Videos', '60p per video', '1 More Video vs OpenAI'],
      yearlyFeatures: ['Up to 100 Videos', '48p per video', '36 More Videos vs OpenAI'],
      popular: false,
      hidden: true, // Tier 1 plans temporarily hidden
      limits: {
        storage: 10,
        users: 1,
        projects: 3,
        apiCalls: 50,
      },
    },
    // Tier 2 - 600 Credits
    {
      id: 'tier-2',
      name: '600 Credits Monthly',
      price: 11,
      yearlyPrice: 105,
      interval: 'month',
      stripePriceIds: {
        monthly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER2_MONTHLY || '',
        yearly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER2_YEARLY || '',
        oneTime: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER2_ONETIME || '',
      },
      description: 'For hobbyists',
      credits: createPlanCredits(600),
      features: ['Up to 20 Videos', 'Only 55p per video', '5 More Videos vs OpenAI'],
      yearlyFeatures: ['Up to 250 Videos', 'Only 42p per video', '110 More Videos vs OpenAI'],
      popular: true,
      limits: {
        storage: 50,
        users: 3,
        projects: 10,
        apiCalls: 200,
      },
    },
    // Tier 3 - 1500 Credits
    {
      id: 'tier-3',
      name: '1500 Credits Monthly',
      price: 22,
      yearlyPrice: 211,
      interval: 'month',
      stripePriceIds: {
        monthly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER3_MONTHLY || '',
        yearly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER3_YEARLY || '',
        oneTime: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER3_ONETIME || '',
      },
      description: 'For content creators',
      credits: createPlanCredits(1500),
      features: ['Up to 50 Videos', 'Only 44p per video', '20 More Videos vs OpenAI'],
      yearlyFeatures: ['Up to 600 Videos', 'Only 35p per video', '320 More Videos vs OpenAI'],
      popular: false,
      limits: {
        storage: 100,
        users: 5,
        projects: 20,
        apiCalls: 500,
      },
    },
    // Tier 4 - 3300 Credits
    {
      id: 'tier-4',
      name: '3300 Credits Monthly',
      price: 44,
      yearlyPrice: 422,
      interval: 'month',
      stripePriceIds: {
        monthly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER4_MONTHLY || '',
        yearly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER4_YEARLY || '',
        oneTime: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER4_ONETIME || '',
      },
      description: 'For professionals',
      credits: createPlanCredits(3300),
      features: ['Up to 110 Videos', 'Only 40p per video', '51 More Videos vs OpenAI'],
      yearlyFeatures: ['Up to 1,333 Videos', 'Only 32p per video', '773 More Videos vs OpenAI'],
      popular: false,
      limits: {
        storage: 200,
        users: 10,
        projects: 50,
        apiCalls: 1000,
      },
    },
    // Tier 5 - 10000 Credits
    {
      id: 'tier-5',
      name: '10000 Credits Monthly',
      price: 99,
      yearlyPrice: 950,
      interval: 'month',
      stripePriceIds: {
        monthly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER5_MONTHLY || '',
        yearly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER5_YEARLY || '',
        oneTime: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER5_ONETIME || '',
      },
      description: 'For agencies',
      credits: createPlanCredits(10000),
      features: ['Up to 333 Videos', 'Only 30p per video', '201 More Videos vs OpenAI'],
      yearlyFeatures: ['Up to 3,833 Videos', 'Only 25p per video', '2,526 More Videos vs OpenAI'],
      popular: false,
      limits: {
        storage: 500,
        users: 20,
        projects: 100,
        apiCalls: 2000,
      },
    },
    // Weekly Plans - tier-w1 through tier-w5
    // Tier W1 - £2.25, 90 Credits Weekly (hidden)
    {
      id: 'tier-w1',
      name: '90 Credits Weekly',
      price: 2.25,
      yearlyPrice: 2.25 * 52,
      interval: 'week',
      stripePriceIds: {
        monthly: '',
        yearly: '',
        weekly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER_W1_WEEKLY || '',
        oneTime: '',
      },
      description: 'Starter weekly',
      credits: {
        monthly: 90,
        yearly: 90 * 52,
        weekly: 90,
        oneTime: 90,
      },
      features: ['Up to 3 Videos', '60p per video'],
      popular: false,
      hidden: true,
      limits: {
        storage: 5,
        users: 1,
        projects: 2,
        apiCalls: 20,
      },
    },
    // Tier W2 - £3.99, 180 Credits Weekly
    {
      id: 'tier-w2',
      name: '180 Credits Weekly',
      price: 3.99,
      yearlyPrice: 3.99 * 52,
      interval: 'week',
      stripePriceIds: {
        monthly: '',
        yearly: '',
        weekly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER_W2_WEEKLY || '',
        oneTime: '',
      },
      description: 'Popular weekly',
      credits: {
        monthly: 180,
        yearly: 180 * 52,
        weekly: 180,
        oneTime: 180,
      },
      features: ['Up to 6 Videos', '55p per video'],
      popular: false,
      limits: {
        storage: 10,
        users: 1,
        projects: 5,
        apiCalls: 50,
      },
    },
    // Tier W3 - £6.99, 350 Credits Weekly
    {
      id: 'tier-w3',
      name: '350 Credits Weekly',
      price: 6.99,
      yearlyPrice: 6.99 * 52,
      interval: 'week',
      stripePriceIds: {
        monthly: '',
        yearly: '',
        weekly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER_W3_WEEKLY || '',
        oneTime: '',
      },
      description: 'Creator weekly',
      credits: {
        monthly: 350,
        yearly: 350 * 52,
        weekly: 350,
        oneTime: 350,
      },
      features: ['Up to 12 Videos', '50p per video'],
      popular: true,
      limits: {
        storage: 25,
        users: 2,
        projects: 10,
        apiCalls: 150,
      },
    },
    // Tier W4 - £12.99, 760 Credits Weekly
    {
      id: 'tier-w4',
      name: '760 Credits Weekly',
      price: 12.99,
      yearlyPrice: 12.99 * 52,
      interval: 'week',
      stripePriceIds: {
        monthly: '',
        yearly: '',
        weekly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER_W4_WEEKLY || '',
        oneTime: '',
      },
      description: 'Pro weekly',
      credits: {
        monthly: 760,
        yearly: 760 * 52,
        weekly: 760,
        oneTime: 760,
      },
      features: ['Up to 27 Videos', '44p per video'],
      popular: false,
      limits: {
        storage: 50,
        users: 3,
        projects: 20,
        apiCalls: 300,
      },
    },
    // Tier W5 - £27.99, 2300 Credits Weekly
    {
      id: 'tier-w5',
      name: '2300 Credits Weekly',
      price: 27.99,
      yearlyPrice: 27.99 * 52,
      interval: 'week',
      stripePriceIds: {
        monthly: '',
        yearly: '',
        weekly: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_TIER_W5_WEEKLY || '',
        oneTime: '',
      },
      description: 'Ultra weekly',
      credits: {
        monthly: 2300,
        yearly: 2300 * 52,
        weekly: 2300,
        oneTime: 2300,
      },
      features: ['Up to 77 Videos', '40p per video'],
      popular: false,
      limits: {
        storage: 100,
        users: 5,
        projects: 50,
        apiCalls: 1000,
      },
    },
  ],

  // Trial configuration
  trial: {
    enabled: FREE_TRIAL_DAYS > 0,
    days: FREE_TRIAL_DAYS,
    plans: ['tier-2', 'tier-3', 'tier-4', 'tier-5'],
  },

  // Invoice configuration
  invoice: {
    footer:
      'Thank you for your business! If you have any questions, please contact our support team.',
    logo: '/images/zappingai-trimmed.png',
    supportEmail: 'support@zappingai.com',
  },

  // Billing configuration
  billing: {
    collectTaxId: true,
    allowPromotionCodes: false,
    automaticTax: true,
  },

  // Feature flags
  features: {
    subscriptions: true,
    oneTimePayments: true,
  },
};
